import { z } from 'zod';

// --- Strona startowa (landing) – cały dokument HTML wklejany przez admina (PRD §10 pkt 37) ---

/** Górna granica wklejki – z zapasem na style i SVG w treści; obrazki mają być linkami, nie `data:`. */
export const LANDING_MAX_LENGTH = 500_000;

export const landingInputSchema = z.object({ html: z.string().max(LANDING_MAX_LENGTH) });
export type LandingInput = z.infer<typeof landingInputSchema>;

/** Panel admina: surowy HTML (pusty = strony startowej nie ma, `/` pokazuje listę edycji). */
export const adminLandingSchema = z.object({ html: z.string().nullable(), updatedAt: z.string().nullable() });
export type AdminLanding = z.infer<typeof adminLandingSchema>;

/** Dla aplikacji: czy jest strona startowa; `version` zmienia adres ramki po każdej zmianie (bez starej kopii w pamięci). */
export const landingStatusSchema = z.object({ available: z.boolean(), version: z.string().nullable() });
export type LandingStatus = z.infer<typeof landingStatusSchema>;

// --- Partnerzy wydarzenia (PRD §10 pkt 38) ---

/**
 * Kolejność sekcji na stronie: organizatorzy, honorowy patronat (PRD §10 pkt 65), sponsorzy konferencji, partnerzy wydarzenia,
 * patroni wydarzenia.
 */
export const partnerCategories = ['organizer', 'honorary_patron', 'sponsor', 'partner', 'patron'] as const;
export const partnerCategorySchema = z.enum(partnerCategories);
export type PartnerCategory = z.infer<typeof partnerCategorySchema>;

/** Logo: PNG, JPEG, WebP, GIF albo SVG, najwyżej 1 MB (typ sprawdza api po zawartości). */
export const PARTNER_LOGO_MAX_BYTES = 1024 * 1024;

const partnerUrl = z
  .string()
  .trim()
  .max(500)
  .refine((v) => v === '' || /^https?:\/\/[^\s]+$/i.test(v), { message: 'Adres musi zaczynać się od http:// lub https://' });

export const partnerInputSchema = z.object({
  category: partnerCategorySchema,
  name: z.string().trim().min(1).max(200),
  url: partnerUrl,
  /** Plik logo w base64 (bez prefiksu `data:`); przy zmianie danych można pominąć – logo zostaje. */
  logoBase64: z.string().max(Math.ceil((PARTNER_LOGO_MAX_BYTES * 4) / 3) + 4).optional(),
});
export type PartnerInput = z.infer<typeof partnerInputSchema>;

export const partnerMoveSchema = z.object({ direction: z.enum(['up', 'down']) });
export type PartnerMove = z.infer<typeof partnerMoveSchema>;

export const partnerSchema = z.object({
  id: z.string().uuid(),
  category: partnerCategorySchema,
  name: z.string(),
  url: z.string().nullable(),
  /** Adres logo z wersją – przeglądarka może je trzymać długo. */
  logoUrl: z.string(),
});
export type Partner = z.infer<typeof partnerSchema>;

export const partnerListSchema = z.object({ partners: z.array(partnerSchema) });
export type PartnerList = z.infer<typeof partnerListSchema>;

// --- Kliknięcia „Powered by CodiFive” w stopce (PRD §10 pkt 62) ---

/** Admin: liczba kliknięć i ostatnie kliknięcia (zalogowani – z kontem, niezalogowani – bez). */
export const poweredByClicksSchema = z.object({
  total: z.number().int(),
  anonymous: z.number().int(),
  loggedIn: z.number().int(),
  accounts: z.number().int(),
  recent: z.array(
    z.object({
      clickedAt: z.string(),
      account: z.object({ id: z.string(), name: z.string(), email: z.string() }).nullable(),
    }),
  ),
});
export type PoweredByClicks = z.infer<typeof poweredByClicksSchema>;
